/**
 * The Properties panel's States section (spec 035): one card per state,
 * collapsed to its header (thumbnail, name, Default badge, summary), one open
 * at a time. An open card chooses between a single image and a spritesheet
 * (D-2: derived from the picture's cut, nothing stored), and for a sheet
 * sets how this scene plays it. Every playback change is one undo step
 * (`sceneStore.setStatePlayback`). The panel says "state", never
 * "expression" (026 D-7).
 *
 * Spec 040 generalises it to any owner of states: a list, a default and
 * callbacks. A sprite passes `sheets` (the spritesheet branch); a
 * background does not, and its open card offers the picture only. Every
 * owner gets "Rename".
 */
import React, { useEffect, useRef, useState } from 'react';
import type { StatePlayback } from '../../../../Shared/src/@types';
import { DEFAULT_PLAYBACK, sheetFrameAt, sheetFrameSize } from '../../../../Shared/src/utils/spriteSheet';
import { useSceneStore } from '../../stores/sceneStore';
import { playedRange, speedLabel } from '../../utils/playbackSpeed';
import { MODE_WORDS, ModeSwitch, WhenDoneSelect, endsAtAll, withMode, withSpeed, withWhenDone } from './PlaybackControls';
import SheetFrame from './SheetFrame';
import SpeedSlider from './SpeedSlider';
import { usePicture } from './usePicture';
import styles from './Sheets.module.scss';

/** One state as the cards show it: a sprite's or a background's (`mediaId` absent or 0: no picture). */
export interface StateItem {
  name: string;
  mediaId?: number;
  playback?: StatePlayback;
}

/** The spritesheet branch (spec 035), for a sprite's states only. */
export interface StatesSheetProps {
  spriteId: string;
  /** "Import spritesheet…" with this state as a single animation's target (034's dialog). */
  onImportSheet: (state: string) => void;
  /** "Pick a sheet from Media…": the picker; a picture with a cut is assigned, one without opens the sheet editor. */
  onPickSheet: (state: string) => void;
  /** The sheet editor on `mediaId`; `assign` points the state at it on Done. */
  onEditSheet: (state: string, mediaId: number, assign: boolean) => void;
}

export interface StatesSectionProps {
  states: readonly StateItem[];
  defaultState?: string;
  onSetDefault: (state: string) => void;
  onDelete: (state: string) => void;
  /** The media picker for a still picture; cancelling changes nothing. */
  onPickMedia: (state: string) => void;
  /** Renames a state; returns the refusal's message, or null when renamed. */
  onRename: (from: string, to: string) => string | null;
  /** Absent: no spritesheet branch (a background's states, spec 040 D-1). */
  sheets?: StatesSheetProps;
}

const SOURCE_BADGE = { json: 'JSON', grid: 'GRID', frames: 'FRAMES' } as const;

/** The open card's Rename: an inline name field; Enter or Save commits, Escape or Cancel leaves it. */
const RenameRow: React.FC<{ name: string; onRename: StatesSectionProps['onRename']; onDone: () => void }> = ({ name, onRename, onDone }) => {
  const [value, setValue] = useState(name);
  const [error, setError] = useState<string | null>(null);
  const commit = () => {
    const refusal = onRename(name, value);
    setError(refusal);
    if (!refusal) onDone();
  };
  return (
    <div className={styles.row}>
      <input
        type="text"
        value={value}
        autoFocus
        aria-label={`Rename ${name}`}
        data-testid="state-rename-input"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') onDone();
        }}
      />
      <button type="button" className={styles.button} onClick={commit} disabled={!value.trim()}>Save</button>
      <button type="button" className={styles.button} onClick={onDone}>Cancel</button>
      {error && <span className={styles.error} role="alert">{error}</span>}
    </div>
  );
};

const StateCard: React.FC<{
  state: StateItem;
  open: boolean;
  onToggle: () => void;
  canDelete: boolean;
} & Omit<StatesSectionProps, 'states'>> = ({ state, open, onToggle, canDelete, defaultState, onSetDefault, onDelete, onPickMedia, onRename, sheets }) => {
  const setStatePlayback = useSceneStore((s) => s.setStatePlayback);
  const mediaId = typeof state.mediaId === 'number' && state.mediaId > 0 ? state.mediaId : 0;
  const picture = usePicture(mediaId);
  const cut = sheets && mediaId > 0 ? picture.cut : undefined;
  const spriteId = sheets?.spriteId ?? '';
  const [wantsSheet, setWantsSheet] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [animating, setAnimating] = useState(false);
  const [frame, setFrame] = useState<number | null>(null);
  const playback: StatePlayback = state.playback ?? DEFAULT_PLAYBACK;
  const isDefault = defaultState === state.name;
  const pictureName = picture.name ?? (mediaId > 0 ? `Picture #${mediaId}` : '');
  const sheet = !!cut;
  const showSheet = sheet || wantsSheet;

  useEffect(() => { if (sheet) setWantsSheet(false); }, [sheet]);
  useEffect(() => {
    if (!open) {
      setAnimating(false);
      setRenaming(false);
    }
  }, [open]);

  // The play button animates the thumbnail until pressed again or the card closes.
  const started = useRef<number | null>(null);
  useEffect(() => {
    if (!animating || !cut || typeof requestAnimationFrame !== 'function') {
      setFrame(null);
      return undefined;
    }
    started.current = null;
    let raf = 0;
    const tick = (now: number) => {
      if (started.current === null) started.current = now;
      setFrame(sheetFrameAt(cut, state.playback, now - started.current, spriteId).index);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animating, cut, state.playback, spriteId]);

  const change = (next: StatePlayback | undefined) => {
    setError(setStatePlayback(spriteId, state.name, next));
  };

  const summary = !sheets
    ? (mediaId > 0 ? pictureName : 'No picture yet')
    : sheet
      ? `Spritesheet · ${speedLabel(playback.speed)} · ${MODE_WORDS[playback.mode]}`
      : wantsSheet ? 'Spritesheet · no sheet yet' : 'Single image';
  const firstFrame = cut ? playedRange(cut, state.playback)[0] : 0;
  const frameSize = cut && picture.natural ? sheetFrameSize(cut, picture.natural) : undefined;

  return (
    <div className={`${styles.card} ${isDefault ? styles.isDefault : ''}`} data-testid="state-card" data-state={state.name}>
      <button type="button" className={styles.cardHeader} aria-expanded={open} onClick={onToggle} data-testid="state-card-header">
        <span className={styles.thumb}>
          <SheetFrame src={picture.src} natural={picture.natural} cut={cut} index={frame ?? firstFrame} size={56} testId="state-card-thumb" />
          {cut && <span className={styles.thumbCount} data-testid="state-card-count">{cut.frameCount}</span>}
        </span>
        <span className={styles.cardTitle}>
          <span className={styles.cardName}>
            <span>{state.name}</span>
            {isDefault && <span className={styles.badge}>Default</span>}
          </span>
          <span className={styles.cardSummary} data-testid="state-card-summary">{summary}</span>
        </span>
      </button>

      {open && (
        <div className={styles.cardBody} data-testid="state-card-body">
          <h5>Picture</h5>
          {sheets && (
            <div className={styles.switch} role="group" aria-label="Picture">
              <button
                type="button"
                aria-pressed={!showSheet}
                onClick={() => {
                  if (sheet) onPickMedia(state.name); // D-2: a still replaces the sheet; cancelling keeps it.
                  else setWantsSheet(false);
                }}
              >
                Single image
              </button>
              <button type="button" aria-pressed={showSheet} onClick={() => { if (!sheet) setWantsSheet(true); }}>
                Spritesheet
              </button>
            </div>
          )}

          {!showSheet && (
            <div className={styles.row}>
              <span data-testid="state-card-picture">{mediaId > 0 ? pictureName : 'No picture yet'}</span>
              <button type="button" className={styles.button} onClick={() => onPickMedia(state.name)}>Change…</button>
            </div>
          )}

          {sheets && wantsSheet && !sheet && (
            <>
              <div className={styles.row}>
                <button type="button" className={styles.button} onClick={() => sheets.onImportSheet(state.name)}>Import spritesheet…</button>
                <button type="button" className={styles.button} onClick={() => sheets.onPickSheet(state.name)}>Pick a sheet from Media…</button>
                {mediaId > 0 && (
                  <button type="button" className={styles.button} onClick={() => sheets.onEditSheet(state.name, mediaId, false)}>
                    Cut {pictureName} as a sheet…
                  </button>
                )}
              </div>
              {mediaId > 0 && (
                <p className={styles.muted} data-testid="state-card-keeps">Switching back to Single image keeps {pictureName}.</p>
              )}
            </>
          )}

          {sheets && sheet && cut && (
            <>
              <div className={styles.row}>
                <span data-testid="state-card-cut">
                  {cut.columns} × {cut.rows} · {cut.frameCount} frames{frameSize ? ` · ${frameSize.width} × ${frameSize.height}` : ''}
                </span>
                <span className={styles.badge}>{SOURCE_BADGE[cut.source ?? cut.cutBy]}</span>
              </div>
              <p className={styles.muted}>Cut on the picture · shared by every state using it</p>
              <h5>Playback in this scene</h5>
              <SpeedSlider
                variant="detail"
                speed={playback.speed}
                cut={cut}
                playback={playback}
                onChange={(v) => change(withSpeed(playback, v))}
              />
              <ModeSwitch mode={playback.mode} onChange={(mode) => change(withMode(playback, mode))} />
              {endsAtAll(playback) && (
                <WhenDoneSelect playback={playback} onChange={(w) => change(withWhenDone(playback, w))} />
              )}
              <div className={styles.row}>
                <button type="button" className={styles.button} onClick={() => sheets.onEditSheet(state.name, mediaId, false)}>Edit sheet…</button>
                <button
                  type="button"
                  className={styles.button}
                  aria-pressed={animating}
                  aria-label={animating ? 'Stop' : 'Play'}
                  onClick={() => setAnimating((a) => !a)}
                  data-testid="state-card-play"
                >
                  {animating ? '■' : '▶'}
                </button>
              </div>
            </>
          )}
          {error && <span className={styles.error} role="alert">{error}</span>}

          {renaming ? (
            <RenameRow name={state.name} onRename={onRename} onDone={() => setRenaming(false)} />
          ) : (
            <div className={styles.row}>
              {!isDefault && (
                <button type="button" className={styles.button} onClick={() => onSetDefault(state.name)}>Set as default</button>
              )}
              <button type="button" className={styles.button} onClick={() => setRenaming(true)} data-testid="state-card-rename">Rename</button>
              {canDelete && (
                <button type="button" className={`${styles.button} ${styles.danger}`} onClick={() => onDelete(state.name)}>Delete</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatesSection: React.FC<StatesSectionProps> = (props) => {
  const { states, onRename, ...rest } = props;
  const [openName, setOpenName] = useState<string | null>(null);
  // A renamed card stays open under its new name.
  const rename = (from: string, to: string) => {
    const refusal = onRename(from, to);
    if (!refusal) setOpenName(to.trim());
    return refusal;
  };
  return (
    <div className={styles.cards} data-testid="state-cards">
      {states.map((state) => (
        <StateCard
          key={state.name}
          {...rest}
          onRename={rename}
          state={state}
          canDelete={states.length > 1}
          open={openName === state.name}
          onToggle={() => setOpenName((n) => (n === state.name ? null : state.name))}
        />
      ))}
    </div>
  );
};

export default StatesSection;
